import React, { Component } from 'react'
import { Alert, Button, Pagination, Radio, Select, Spin, message } from 'antd'
import moment from 'moment'
import {
    embroideryDashboardOverview, embroideryDashboardItems, embroideryDashboardItemsExportCsv, embroideryDashboardFilterOptions,
} from '../../../../services/api/3dApi'
import downloadFileFromData from '../../../../helpers/downloadFileFromData'
import EmbroideryOverviewMatrix from './EmbroideryOverviewMatrix'
import EmbroideryFilterBar from './EmbroideryFilterBar'
import EmbroideryItemsTable from './EmbroideryItemsTable'
import EmbroideryBatchGroups from './EmbroideryBatchGroups'
import { DEFAULT_FILTERS, applyFilterChange, getOverviewParams, getItemsParams } from './filterState'
import store from 'store'
import { getUrlParam, setUrlParam } from '../dashboardUrlState'
import { OVERDUE_STAGE_OPTIONS, STAGE_LABELS, TIERS, TIER_META, formatNumber } from './embroideryFormat'

const EmbroideryProductionContainer = React.lazy(() => import('./EmbroideryProductionContainer'))

const REFRESH_MS = 60000
const OVERVIEW_KEYS = ['sku', 'partner', 'q'] // bộ lọc làm đổi số ở ma trận
const PAGE_SIZE_ITEM = 50
const VIEWS = ['status', 'output']
const VIEW_STORE_KEY = '3d_dashboard_embroidery_view'

// Tab con: ?view= trên địa chỉ (link gửi theo tab) → tab đã nhớ → 'status'. F5 không bị nhảy về mặc định
const getInitialView = () => {
    const urlView = getUrlParam('view')
    if (VIEWS.includes(urlView)) return urlView
    const stored = store.get(VIEW_STORE_KEY)
    return VIEWS.includes(stored) ? stored : 'status'
}

// Tab "Sản xuất thêu" — phần Trạng thái sản xuất (Release A). Phần Sản lượng làm ở Release B.
class EmbroideryProductionPage extends Component {
    state = {
        view: getInitialView(),
        filters: Object.assign({}, DEFAULT_FILTERS),
        qResetKey: 0, // đổi key để FilterBar remount → huỷ debounce và xoá ô tìm đang gõ dở khi bộ lọc bị xoá từ bên ngoài
        groupBy: 'item',
        page: 1,
        overview: null,
        items: null,
        generatedAt: null,
        loading: true,
        exporting: false,
        error: '',
        skuOptions: [],
        partnerOptions: [],
    }

    componentDidMount() {
        setUrlParam('view', this.state.view)
        this._fetch(true)
        // Tab trình duyệt đang ẩn thì không tự làm mới; quay lại tab thì tải ngay một lần
        this.timer = setInterval(() => { if (!document.hidden) this._fetch(false) }, REFRESH_MS)
        document.addEventListener('visibilitychange', this._onVisibilityChange)
    }

    componentWillUnmount() {
        clearInterval(this.timer)
        document.removeEventListener('visibilitychange', this._onVisibilityChange)
        this.unmounted = true
    }

    _onVisibilityChange = () => {
        if (!document.hidden) this._fetch(false)
    }

    // SKU thêu + partner của các lô đang có item thêu (cùng id với bộ lọc partner)
    _applyOptions = (options) => {
        this.setState({
            skuOptions: options.skus || [],
            partnerOptions: (options.partners || []).map((p) => ({ value: p.id, label: p.name })),
        })
    }

    // Dự phòng: overview đã kèm filter_options nên chỉ gọi API riêng khi lần tải đầu không có field đó
    _loadOptions = async () => {
        try {
            const { data: response } = await embroideryDashboardFilterOptions()
            if (this.unmounted || this.optionsFromOverview) return
            this._applyOptions(response.data || {})
        } catch (e) {
            // Bộ lọc vẫn dùng được khi không tải được danh sách gợi ý
        }
    }

    // showSpinner = false cho lần tự làm mới: không nhấp nháy, giữ nguyên bộ lọc và trang đang xem (FR-010)
    // withOverview = false: chỉ tải lại danh sách (đổi trang, nhóm, công đoạn, mức hạn không làm đổi số ở ma trận — BR-1.3) → bớt một request
    _fetch = async (showSpinner, needOverview = true) => {
        if (this.state.view !== 'status') return
        const nextCount = (this.requestCount || 0) + 1
        this.requestCount = nextCount
        // Lần tải ma trận trước bị lần gọi mới chen ngang (kết quả bị bỏ) thì lần này phải tải lại ma trận
        const withOverview = needOverview || !!this.overviewPending
        if (withOverview) this.overviewPending = true
        const { filters, groupBy, page } = this.state
        if (showSpinner) this.setState({ loading: true })

        try {
            const [ovRes, itRes] = await Promise.all([
                withOverview ? embroideryDashboardOverview(getOverviewParams(filters)) : null,
                embroideryDashboardItems(getItemsParams(filters, { group_by: groupBy, page, limit: PAGE_SIZE_ITEM })),
            ])
            if (this.unmounted || nextCount !== this.requestCount) return // BR-10.2: bỏ kết quả của lần gọi cũ

            const ov = ovRes && ovRes.data
            if (ov && ov.success) this.overviewPending = false
            if (ov && ov.success && ov.data.filter_options) {
                this.optionsFromOverview = true
                this._applyOptions(ov.data.filter_options)
            } else if (ov && ov.success && !this.optionsFallbackRequested) {
                this.optionsFallbackRequested = true
                this._loadOptions()
            }
            const it = itRes.data
            if ((ov && !ov.success) || !it.success) throw new Error((ov && ov.error) || it.error || 'Không tải được dữ liệu')

            // Tự làm mới khi tổng giảm có thể làm trang đang xem vượt quá trang cuối → API trả trang rỗng.
            // Kẹp về trang cuối rồi tải lại danh sách (ma trận đã có kết quả mới nên giữ lại)
            const total = it.data.group_by === 'batch' ? it.data.total_groups : it.data.total
            const lastPage = Math.max(1, Math.ceil(total / it.data.limit))
            if (page > lastPage) {
                this.setState((state) => ({
                    overview: ov ? ov.data : state.overview,
                    generatedAt: ov ? ov.data.generated_at : state.generatedAt,
                    page: lastPage,
                }), () => this._fetch(false, false))
                return
            }

            this.setState((state) => ({
                overview: ov ? ov.data : state.overview,
                items: it.data,
                generatedAt: ov ? ov.data.generated_at : it.data.generated_at,
                loading: false,
                error: '',
            }))
        } catch (e) {
            if (this.unmounted || nextCount !== this.requestCount) return
            // EC-I-02: báo lỗi rõ ràng, không hiển thị số 0 gây hiểu nhầm
            this.setState({ loading: false, error: e.message || 'Không tải được dữ liệu' })
        }
    }

    _changeView = (event) => {
        const view = event.target.value
        this.setState({ view }, () => {
            store.set(VIEW_STORE_KEY, view)
            setUrlParam('view', view)
            if (view === 'status') this._fetch(true)
        })
    }

    _changeFilters = (change) => {
        const prevFilters = this.state.filters
        const result = applyFilterChange(prevFilters, change)
        if (result.clearedOthers) message.info('Đã xoá các bộ lọc khác — đang lọc theo quá hạn công đoạn')
        // Chọn "Quá hạn công đoạn" xoá ô tìm; ô có thể đang gõ dở (prop q chưa đổi) nên cần remount FilterBar
        this.setState(({ qResetKey }) => ({
            filters: result.filters,
            page: 1,
            qResetKey: change.overdue_stage ? qResetKey + 1 : qResetKey,
        }), () => this._fetch(true, OVERVIEW_KEYS.some((key) => this.state.filters[key] !== prevFilters[key])))
    }

    _clearFilters = () => {
        this.setState(({ qResetKey }) => ({ filters: Object.assign({}, DEFAULT_FILTERS), page: 1, qResetKey: qResetKey + 1 }), () => this._fetch(true))
    }

    // Xuất CSV toàn bộ item khớp bộ lọc đang áp (không gửi page/limit). BE trả chuỗi CSV; lỗi (tập rỗng, tham số sai) trả JSON.
    // Lỗi chỉ báo thông báo, không đụng tới danh sách và ma trận đang hiện (EC-I-05)
    _exportCsv = async () => {
        if (this.state.exporting) return
        const { filters, groupBy, items } = this.state
        // Tập lọc rỗng: chặn xuất và báo (BR-7.2, EC-O-05)
        if (!items || !items.total) {
            message.warning('Không có item nào để xuất')
            return
        }
        this.setState({ exporting: true })
        try {
            const { data } = await embroideryDashboardItemsExportCsv(getItemsParams(filters, { group_by: groupBy }))
            if (typeof data !== 'string') throw new Error((data && data.error) || 'Không xuất được file CSV')
            downloadFileFromData(data, { filename: `item-theu-${moment().utcOffset(7).format('HH_mm_DD_MM_YYYY')}.csv` })
        } catch (e) {
            message.error(e.message || 'Không xuất được file CSV. Vui lòng thử lại.')
        } finally {
            if (!this.unmounted) this.setState({ exporting: false })
        }
    }

    _changeGroupBy = (event) => {
        this.setState({ groupBy: event.target.value, page: 1 }, () => this._fetch(true, false))
    }

    _changePage = (page) => {
        this.setState({ page }, () => this._fetch(true, false))
    }

    _getTitle = () => {
        const { filters, items } = this.state
        const parts = [`Danh sách item — ${filters.stage ? STAGE_LABELS[filters.stage] : 'Tất cả công đoạn'}`]
        if (filters.deadline) parts.push(TIER_META[filters.deadline].label)
        const overdue = OVERDUE_STAGE_OPTIONS.find((o) => o.value === filters.overdue_stage)
        if (overdue) parts.push(overdue.label.split(' (')[0])
        return `${parts.join(' · ')} (${formatNumber(items ? items.total : 0)} items)`
    }

    _renderStatus() {
        const { filters, groupBy, page, overview, items, loading, exporting, error, skuOptions, partnerOptions, qResetKey } = this.state

        return (
            <div className='panelBody'>
                {error && (
                    <Alert
                        type='error'
                        showIcon
                        className='mb-3'
                        message={error}
                        description={<Button size='small' onClick={() => this._fetch(true)}>Thử lại</Button>}
                    />
                )}

                <EmbroideryFilterBar
                    key={qResetKey}
                    filters={filters}
                    disabled={loading}
                    skuOptions={skuOptions}
                    partnerOptions={partnerOptions}
                    onChange={this._changeFilters}
                    onClear={this._clearFilters}
                />

                <Spin spinning={loading} tip='Đang tải...'>
                    {overview && overview.configured === false && (
                        <Alert type='warning' showIcon className='mb-3' message='Chưa cấu hình quy trình Thêu hoặc SKU thêu' />
                    )}

                    {overview && (
                        <div className='section'>
                            <div className='secTitle small'>
                                <h3>Tổng quan item thêu cần sản xuất</h3>
                                <p>Đếm theo trạng thái <b>từng item</b> (<span className='mono'>BatchItem.status</span>) · bấm một ô để lọc danh sách</p>
                            </div>
                            <EmbroideryOverviewMatrix overview={overview} filters={filters} onSelect={this._changeFilters} />
                        </div>
                    )}

                    {items && (
                        <div className='section'>
                            <div className='secTitle'>
                                <h3>{this._getTitle()}</h3>
                                <div className='listControls'>
                                    <Select
                                        className={filters.overdue_stage ? 'overdueActive' : ''}
                                        value={filters.overdue_stage || ''}
                                        onChange={(value) => this._changeFilters({ overdue_stage: value })}
                                    >
                                        <Select.Option value=''>Quá hạn công đoạn: Tất cả</Select.Option>
                                        {OVERDUE_STAGE_OPTIONS.map((o) => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
                                    </Select>
                                    <Radio.Group
                                        buttonStyle='solid'
                                        value={filters.deadline || 'all'}
                                        onChange={(e) => this._changeFilters({ deadline: e.target.value === 'all' ? '' : e.target.value })}
                                    >
                                        <Radio.Button value='all'>Tất cả</Radio.Button>
                                        {TIERS.map((tier) => <Radio.Button key={tier} value={tier}>{TIER_META[tier].label}</Radio.Button>)}
                                    </Radio.Group>
                                    <Radio.Group value={groupBy} onChange={this._changeGroupBy} buttonStyle='solid'>
                                        <Radio.Button value='item'>Theo item</Radio.Button>
                                        <Radio.Button value='batch'>Nhóm theo lô</Radio.Button>
                                    </Radio.Group>
                                    <Button
                                        icon='download'
                                        loading={exporting}
                                        disabled={loading}
                                        onClick={this._exportCsv}
                                    >
                                        Xuất CSV
                                    </Button>
                                </div>
                            </div>
                            {items.group_by === 'batch'
                                ? <EmbroideryBatchGroups groups={items.groups} overdueActive={!!filters.overdue_stage} />
                                : <EmbroideryItemsTable items={items.items} />}
                            {(items.group_by === 'batch' ? items.total_groups : items.total) > 0 && (
                            <Pagination
                                className='mt-3 text-right EmbroideryProductionPagination'
                                current={page}
                                pageSize={items.limit}
                                total={items.group_by === 'batch' ? items.total_groups : items.total}
                                onChange={this._changePage}
                                showTotal={(total, range) => (items.group_by === 'batch'
                                    ? `${formatNumber(total)} lô · ${formatNumber(items.total)} item · ${items.limit} lô/trang`
                                    : `Hiển thị ${formatNumber(range[0])}–${formatNumber(range[1])} của ${formatNumber(total)} item · sắp xếp theo mức quá hạn`)}
                            />
                            )}
                        </div>
                    )}
                </Spin>
            </div>
        )
    }

    render() {
        const { view, generatedAt } = this.state

        return (
            <div className='EmbroideryProduction ml-3 mr-3 pb-3 pl-3 pr-3'>
                <div className='EmbroideryProductionInner p-3'>
                <div className='panelHead'>
                    <Radio.Group size='large' value={view} onChange={this._changeView} buttonStyle='solid'>
                        <Radio.Button value='status'>Trạng thái sản xuất</Radio.Button>
                        <Radio.Button value='output'>Sản lượng</Radio.Button>
                    </Radio.Group>
                    {view === 'status' && (
                        <div className='refreshInfo'>
                            <span className='liveDot' />
                            Tự cập nhật mỗi 60 giây{generatedAt ? ` · cập nhật lúc ${moment(generatedAt).utcOffset(7).format('HH:mm')}` : ''}
                        </div>
                    )}
                </div>
                {view === 'status'
                    ? this._renderStatus()
                    : (
                        // Tách chunk để thư viện biểu đồ (recharts) không làm nặng bundle chính
                        <React.Suspense fallback={<Spin className='d-block my-5' />}>
                            <EmbroideryProductionContainer />
                        </React.Suspense>
                    )}
                </div>
            </div>
        )
    }
}

export default EmbroideryProductionPage
